/**
 * PromptsSettings
 * AI prompt template editors — analysis, planning, and agent task templates.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Save, RotateCcw, Check, RotateCw } from 'lucide-react';
import { Panel, PanelHeader, PanelBody } from '../shared/Panel';
import { useAgentConfig, useUpdateAgentConfig, type AgentConfig, type AgentConfigUpdate } from '../../api/workspace-agent-config';
import { useWorkspace } from '../../contexts/WorkspaceContext';

// ─── Prompt Textarea ────────────────────────────────────────────

function PromptEditor({
  label,
  description,
  value,
  defaultValue,
  onChange,
  onReset,
}: {
  label: string;
  description?: string;
  value: string;
  defaultValue: string;
  onChange: (v: string) => void;
  onReset: () => void;
}) {
  const isCustomized = value !== defaultValue && value !== '';

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-[12px] font-medium text-text">{label}</span>
          {description && (
            <p className="text-[11px] text-subtle">{description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
            isCustomized ? 'bg-coral-50 text-coral-600' : 'bg-surface-subtle text-subtle'
          }`}>
            {isCustomized ? 'customized' : 'default'}
          </span>
          {isCustomized && (
            <button
              onClick={onReset}
              className="text-[11px] text-subtle hover:text-muted flex items-center gap-0.5"
              title="Reset to default"
            >
              <RotateCw size={10} />
              Reset
            </button>
          )}
        </div>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        className="w-full px-3 py-2 border border-line rounded-lg text-text bg-white font-mono text-[12px] leading-relaxed focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none resize-y"
        placeholder={defaultValue || 'Enter prompt template...'}
      />
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────

interface PromptsSettingsProps {
  sectionRefs?: Record<string, React.RefObject<HTMLDivElement>>;
}

export const PromptsSettings: React.FC<PromptsSettingsProps> = ({ sectionRefs }) => {
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;

  const { data: config, isLoading } = useAgentConfig(workspaceId);
  const updateMutation = useUpdateAgentConfig();

  const [changes, setChanges] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  useEffect(() => {
    setChanges({});
  }, [config]);

  const getPrompt = useCallback((key: keyof NonNullable<AgentConfig['prompts']>) => {
    return changes[key] ?? config?.prompts?.[key] ?? '';
  }, [changes, config]);

  const setPrompt = useCallback((key: string, value: string) => {
    setChanges(prev => ({ ...prev, [key]: value }));
    setSaveStatus('idle');
  }, []);

  const resetPrompt = useCallback((key: string) => {
    setChanges(prev => ({ ...prev, [key]: '' }));
    setSaveStatus('idle');
  }, []);

  const hasChanges = Object.keys(changes).length > 0;

  const handleSave = useCallback(async () => {
    if (!workspaceId || !hasChanges) return;
    setSaveStatus('saving');
    try {
      await updateMutation.mutateAsync({
        workspaceId,
        config: { prompts: changes } as AgentConfigUpdate,
      });
      setChanges({});
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('idle');
    }
  }, [workspaceId, hasChanges, changes, updateMutation]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map(i => <div key={i} className="h-48 bg-surface-subtle rounded-lg animate-pulse" />)}
      </div>
    );
  }

  const defaults = config?.prompts || {};

  return (
    <div className="space-y-6">
      {/* Analysis Prompts */}
      <div ref={sectionRefs?.['Analysis']}>
        <Panel>
          <PanelHeader title="Analysis Prompts" />
          <PanelBody>
            <div className="space-y-5">
              <PromptEditor
                label="Content Analyzer"
                description="Sprint readiness reviewer — scores issue quality"
                value={getPrompt('contentAnalyzer')}
                defaultValue={defaults.contentAnalyzer || ''}
                onChange={(v) => setPrompt('contentAnalyzer', v)}
                onReset={() => resetPrompt('contentAnalyzer')}
              />
              <PromptEditor
                label="Readiness Resolver"
                description="Sprint preparation agent — improves story documentation"
                value={getPrompt('readinessResolver')}
                defaultValue={defaults.readinessResolver || ''}
                onChange={(v) => setPrompt('readinessResolver', v)}
                onReset={() => resetPrompt('readinessResolver')}
              />
              <PromptEditor
                label="Visual QA"
                description="Figma vs implementation comparison"
                value={getPrompt('visualQA')}
                defaultValue={defaults.visualQA || ''}
                onChange={(v) => setPrompt('visualQA', v)}
                onReset={() => resetPrompt('visualQA')}
              />
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* Planning Prompts */}
      <div ref={sectionRefs?.['Planning']}>
        <Panel>
          <PanelHeader title="Planning Prompts" />
          <PanelBody>
            <div className="space-y-5">
              <PromptEditor
                label="Document Analysis"
                description="Analyzes spec documents for epic generation"
                value={getPrompt('documentAnalysis')}
                defaultValue={defaults.documentAnalysis || ''}
                onChange={(v) => setPrompt('documentAnalysis', v)}
                onReset={() => resetPrompt('documentAnalysis')}
              />
              <PromptEditor
                label="Epic Generation"
                description="Generates epics from analyzed documents"
                value={getPrompt('epicGeneration')}
                defaultValue={defaults.epicGeneration || ''}
                onChange={(v) => setPrompt('epicGeneration', v)}
                onReset={() => resetPrompt('epicGeneration')}
              />
              <PromptEditor
                label="Story Generation"
                description="Breaks epics into implementable stories"
                value={getPrompt('storyGeneration')}
                defaultValue={defaults.storyGeneration || ''}
                onChange={(v) => setPrompt('storyGeneration', v)}
                onReset={() => resetPrompt('storyGeneration')}
              />
              <PromptEditor
                label="Agent Task Template"
                description="Claude agent assignment prompt template"
                value={getPrompt('agentTaskTemplate')}
                defaultValue={defaults.agentTaskTemplate || ''}
                onChange={(v) => setPrompt('agentTaskTemplate', v)}
                onReset={() => resetPrompt('agentTaskTemplate')}
              />
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* Martha Chat */}
      <div ref={sectionRefs?.['Martha']}>
        <Panel>
          <PanelHeader title="Martha Chat" />
          <PanelBody>
            <div className="space-y-5">
              <PromptEditor
                label="System Prompt"
                description="Overrides Martha's default system prompt. Leave blank for built-in prompt."
                value={getPrompt('chatSystemPrompt')}
                defaultValue={defaults.chatSystemPrompt || ''}
                onChange={(v) => setPrompt('chatSystemPrompt', v)}
                onReset={() => resetPrompt('chatSystemPrompt')}
              />
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* Save Bar */}
      {hasChanges && (
        <div className="sticky bottom-4 z-10">
          <div className="bg-white border border-line rounded-xl shadow-lg px-4 py-3 flex items-center justify-between">
            <span className="text-[13px] text-muted">Unsaved prompt changes</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setChanges({}); setSaveStatus('idle'); }}
                className="px-3 py-1.5 text-[13px] text-muted hover:text-text rounded-lg hover:bg-surface-subtle transition-colors"
              >
                <RotateCcw size={14} className="inline mr-1" />
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={saveStatus === 'saving'}
                className="px-4 py-1.5 text-[13px] font-medium text-white bg-coral-500 hover:bg-coral-600 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                {saveStatus === 'saving' ? (
                  <span className="inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : saveStatus === 'saved' ? (
                  <Check size={14} />
                ) : (
                  <Save size={14} />
                )}
                {saveStatus === 'saved' ? 'Saved' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
